import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Paper, Typography, CircularProgress } from "@material-ui/core";
import RegistrationFormStep from "./RegistrationFormStep";
import _ from "lodash";
import { api } from "../shared/utilities/api";
import apiConfigs from "../shared/utilities/apiConfigs";
import { validateInviteToken } from "./registerApi";

const DEFAULT_LOGO = "https://porton-shared.s3.ca-central-1.amazonaws.com/images/Porton_New_Logo.png";

const useStyles = makeStyles((theme) => ({
    errorRoot: {
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#F5F6FA",
        padding: theme.spacing(2),
    },
    errorCard: {
        padding: theme.spacing(4),
        maxWidth: 420,
        width: "100%",
        textAlign: "center",
        borderRadius: 8,
    },
    errorTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(1),
    },
    errorBody: {
        color: theme.palette.text.secondary,
        fontSize: 14,
    },
    loadingRoot: {
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#F5F6FA",
    },
}));

function ErrorPanel({ title, body }) {
    const classes = useStyles();
    return (
        <Box className={classes.errorRoot}>
            <Paper elevation={2} className={classes.errorCard}>
                <Typography variant="h6" className={classes.errorTitle}>{title}</Typography>
                <Typography className={classes.errorBody}>{body}</Typography>
            </Paper>
        </Box>
    );
}

export default function RegisterPage() {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("t");
    const orgId = params.get("o");

    const [step, setStep] = useState("validating"); // "validating" | "form" | "expired" | "error"
    const [expiredMsg, setExpiredMsg] = useState("");
    const [logo, setLogo] = useState(DEFAULT_LOGO);
    const [orgInfo, setOrgInfo] = useState({});

    useEffect(() => {
        if (!orgId) return;
        const fetchOrg = async () => {
            try {
                const result = await api(apiConfigs.baseOption("get", `orgs/${orgId}/getReport`));
                if (result.status === 200) {
                    const orgLogo = _.get(result, "data.logo", DEFAULT_LOGO);
                    if (orgLogo) setLogo(orgLogo);
                    setOrgInfo({
                        address: _.get(result, "data.address", ""),
                        contactNum: _.get(result, "data.contactNum", ""),
                        name: _.get(result, "data.name", ""),
                    });
                }
            } catch (err) {
                // fail silently — page still works without org branding
            }
        };
        fetchOrg();
    }, [orgId]);

    useEffect(() => {
        if (!token || !orgId) {
            setStep("error");
            return;
        }

        const validate = async () => {
            try {
                const result = await validateInviteToken(token, orgId);

                if (result && result.status === 200 && result.data?.valid) {
                    setStep("form");
                    return;
                }

                const data = result?.data || result?.error?.response?.data || {};
                const msg = data.message || "";
                const isDeadLink =
                    result?.status === 410 ||
                    data.expired === true ||
                    (typeof msg === "string" && /expired|already been used|has been used|no longer valid/i.test(msg));

                if (isDeadLink) {
                    setExpiredMsg("This invitation link has already been used or has expired. Please contact your clinic for a new invitation.");
                    setStep("expired");
                } else {
                    setExpiredMsg(msg || "This invitation link is not valid. Please contact your clinic.");
                    setStep("expired");
                }
            } catch (err) {
                setStep("error");
            }
        };

        validate();
    }, [token, orgId]);

    const handleSuccess = (intakeToken, returnedOrgId) => {
        window.location.assign(`/intake?t=${intakeToken}&o=${returnedOrgId}`);
    };

    if (!token || !orgId) {
        return (
            <ErrorPanel
                title="Invalid link"
                body="Please use the link provided in your invitation email. If you believe this is an error, contact your clinic."
            />
        );
    }

    if (step === "validating") {
        return (
            <Box style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#F5F6FA" }}>
                <CircularProgress style={{ color: "#C2651A" }} />
            </Box>
        );
    }

    if (step === "expired" || step === "error") {
        return (
            <ErrorPanel
                title="Link unavailable"
                body={expiredMsg || "This invitation link is not valid. Please contact your clinic for assistance."}
            />
        );
    }

    return (
        <RegistrationFormStep
            token={token}
            orgId={orgId}
            logo={logo}
            orgInfo={orgInfo}
            onSuccess={handleSuccess}
        />
    );
}
